import { Avatar, Badge, Group, Stack, Table, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { i18nStore } from "@/store/i18n";
import type { AssistantLogUserRowDto } from "@/types/ai-assistant-log";
import {
	formatLogCost,
	formatLogCount,
	formatLogRelative,
	formatLogTokens,
	logDisplayName,
	logInitials,
} from "./log-format.logic";
import { LogVendorBadge } from "./log-vendor-badge";

/**
 * Tabel "Per Pengguna" (lapis 1). Satu baris = satu pengguna; barisnya bisa
 * diklik untuk membuka lapis 2. Angka rata kanan, identitas & model rata kiri,
 * error di tengah — mengikuti rancangan 08 §3.
 */
export function LogUsersTable({ rows }: { rows: AssistantLogUserRowDto[] }) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const navigate = useNavigate();

	const open = (userId: string) =>
		navigate({
			to: "/admin/ai-assistant/log/$userId",
			params: { userId },
		});

	return (
		<Table.ScrollContainer minWidth={900}>
			<Table highlightOnHover verticalSpacing="sm" data-log-users-table>
				<Table.Thead>
					<Table.Tr>
						<Table.Th>{t.colUser}</Table.Th>
						<Table.Th ta="right">{t.colConversations}</Table.Th>
						<Table.Th ta="right">{t.colTurns}</Table.Th>
						<Table.Th ta="right">{t.colTokens}</Table.Th>
						<Table.Th ta="right">{t.colCost}</Table.Th>
						<Table.Th>{t.colDominantModel}</Table.Th>
						<Table.Th ta="center">{t.colErrors}</Table.Th>
						<Table.Th ta="right">{t.colLastActive}</Table.Th>
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{rows.map((row) => (
						<Table.Tr
							key={row.userId}
							style={{ cursor: "pointer" }}
							onClick={() => open(row.userId)}
							aria-label={t.openUser}
						>
							<Table.Td>
								<Group gap="sm" wrap="nowrap">
									<Avatar color="orange" radius="xl" size="md">
										{logInitials(row.name, row.email)}
									</Avatar>
									<Stack gap={0}>
										<Text size="sm" fw={600} lineClamp={1}>
											{logDisplayName(row.name, row.email)}
										</Text>
										<Text size="xs" c="dimmed" lineClamp={1}>
											{row.email}
										</Text>
									</Stack>
								</Group>
							</Table.Td>
							<Table.Td ta="right">
								{formatLogCount(row.conversations, lang)}
							</Table.Td>
							<Table.Td ta="right">{formatLogCount(row.turns, lang)}</Table.Td>
							<Table.Td ta="right">
								{formatLogTokens(row.inputTokens, row.outputTokens, lang)}
							</Table.Td>
							<Table.Td ta="right">{formatLogCost(row.cost, t, lang)}</Table.Td>
							<Table.Td>
								{row.dominantModel ? (
									<LogVendorBadge use={row.dominantModel} />
								) : (
									<Text size="sm" c="dimmed">
										{t.none}
									</Text>
								)}
							</Table.Td>
							<Table.Td ta="center">
								{row.errors > 0 ? (
									<Badge color="red" variant="light">
										{formatLogCount(row.errors, lang)}
									</Badge>
								) : (
									<Text size="sm" c="dimmed">
										{t.none}
									</Text>
								)}
							</Table.Td>
							<Table.Td ta="right">
								<Text size="sm" c="dimmed">
									{formatLogRelative(row.lastActiveAt, lang)}
								</Text>
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}
